import { useCallback, useRef } from 'react';

import type { PersonalizationStep } from '../../../contracts';
import type { EditActivity, MicTestPhase } from '../learnBasicsTypes';

const FRESH_EDIT_ACTIVITY: EditActivity = {
  updatedStyle: false,
  usedEvidence: false,
  createdDocument: false,
};

interface UseOnboardingNavigationHandlersParams {
  currentStep: PersonalizationStep;
  generatedTemplateDisplayName: string;
  goToStep: (step: PersonalizationStep) => void;
  goToPreviousStep: () => void;
  setMicPhase: (phase: MicTestPhase) => void;
  setMicTranscript: (value: string) => void;
  setMicRecordingDuration: (value: number) => void;
  setMicEditActivity: (activity: EditActivity) => void;
}

/**
 * Owns the onboarding header Back behaviour plus the note-edit back channel:
 * `registerNoteEditBackHandler` lets the stepped note-edit sub-flow consume the
 * Back press before it leaves the step (ref-backed, so registering never
 * re-renders consumers).
 */
export function useOnboardingNavigationHandlers({
  currentStep,
  generatedTemplateDisplayName,
  goToStep,
  goToPreviousStep,
  setMicPhase,
  setMicTranscript,
  setMicRecordingDuration,
  setMicEditActivity,
}: UseOnboardingNavigationHandlersParams) {
  const noteEditBackRef = useRef<(() => boolean) | null>(null);
  const registerNoteEditBackHandler = useCallback((handler: (() => boolean) | null) => {
    noteEditBackRef.current = handler;
  }, []);

  const resetMicTestState = useCallback(() => {
    // Reset every piece of mic-test state so re-entering the step looks exactly
    // like a fresh visit (preview returns to its idle skeleton, user re-records
    // into a clean transcript). `hasCompleted` stays sticky on purpose.
    setMicPhase('recording');
    setMicTranscript('');
    setMicRecordingDuration(0);
    setMicEditActivity(FRESH_EDIT_ACTIVITY);
  }, [setMicPhase, setMicTranscript, setMicRecordingDuration, setMicEditActivity]);

  const handleBack = useCallback(() => {
    if (currentStep === 'document-selection' && !generatedTemplateDisplayName) {
      goToStep('template-creation');
      return;
    }
    // Give the stepped note-edit sub-flow first refusal on the back press.
    if (currentStep === 'note-edit' && noteEditBackRef.current?.()) return;
    // `tour-detail` is the guided-tour arm's first post-transcript screen, so
    // going back from it means re-recording — same as `note-edit` in control.
    // The later tour screens step back through each other normally.
    if (
      currentStep === 'note-ready' ||
      currentStep === 'note-edit' ||
      currentStep === 'tour-detail'
    ) {
      resetMicTestState();
      goToStep('mic-test');
      return;
    }
    goToPreviousStep();
  }, [
    currentStep,
    generatedTemplateDisplayName,
    goToStep,
    goToPreviousStep,
    resetMicTestState,
  ]);

  return {
    handleBack,
    registerNoteEditBackHandler,
  };
}
